<script lang="ts" setup>
// import { computed, onMounted, reactive, ref, watch, watchEffect } from 'vue';
import { WindowCloseIcon } from 'mdi-vue3'
import { computed, type HTMLAttributes, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { ADialogBase, ADialogCard, type DialogCardFooterInterface } from '../../../../'

const { t } = useI18n({
  messages: {
    en: {
      confirm: 'Confirm',
      cancel: 'Cancel',
    },
    nl: {
      confirm: 'Bevestigen',
      cancel: 'Annuleren',
    },
  },
})

defineOptions({ inheritAttrs: false })

defineExpose({
  setLoading,
  setSuccess,
  setError,
  close,
})

type ADialogModel = never

export type ADialogConfirmEvent = {
  setLoading: (isLoading?: boolean) => void
  setSuccess: (message: string) => void
  setError: (message?: unknown) => void
  close: () => void
}

export interface ADialogProperties {
  title?: string
  description?: string
  text?: string
  class?: HTMLAttributes['class']
  actions?: DialogCardFooterInterface[]
  cancel?: boolean | (() => void) | string | DialogCardFooterInterface | undefined
  confirm?: boolean | ((event: ADialogConfirmEvent) => boolean | void) | string | DialogCardFooterInterface | undefined
  beforeConfirm?: () => void
  afterConfirm?: () => void
  contentCollapse?: boolean
  persistent?: boolean
  type?: string
}

export type ADialogExpose = ADialogConfirmEvent

type ADialogSlots = never

export type ADialogTypes = {
  model: ADialogModel
  properties: ADialogProperties
  emits: ADialogEmits
  expose: ADialogExpose
  slots: ADialogSlots
}

export type ADialogState = 'idle' | 'loading' | 'success' | 'error'

// Props
const properties = withDefaults(defineProps<ADialogProperties>(), {
  title: '',
  description: '',
  text: '',
  class: undefined,
  actions: undefined,
  cancel: undefined,
  confirm: undefined,
  beforeConfirm: undefined,
  afterConfirm: undefined,
  contentCollapse: undefined,
  persistent: undefined,
  type: 'default',
})
// end Props

// Emits
export interface ADialogEmits {
  (event: 'clickOutsideOrKeyupEscape'): void
  (event: 'close'): void
}

const emit = defineEmits<ADialogEmits>()
// end Emits

// Refs
const state = ref<ADialogState>('idle')
// end Refs

// Computed
const computedActions = computed(() => {
  const length = properties.actions?.length ?? 0
  const actions: DialogCardFooterInterface[] = []

  if (properties.cancel) {
    actions.push(createCancelButton())
    // console.log(createCancelButton())
  }

  if (properties.actions) {
    for (const [index, action] of properties.actions.entries()) {
      actions.push({
        ...action,
        /** Sets the last button variant automatically to outline if it did not specify it's variant. */
        variant: action.variant === undefined ? (index === length - 1 ? 'outline' : 'closer') : action.variant,
      })
    }
  }

  if (properties.confirm) {
    actions.push(createConfirmButton())
  }
  return actions
})

// end Computed

// Functions
function onClickCancel(): void {
  if (typeof properties.cancel === 'function') {
    properties.cancel()
    return
  }

  emit('close')
}

async function onClickConfirm(): Promise<void> {
  if (typeof properties.confirm === 'function') {
    const result = properties.confirm({
      setLoading,
      setSuccess,
      setError,
      close,
    })
    if (result === false) return
  }

  emit('close')
}

function onClickOutsideOrKeyupEscape(): void {
  if (properties.persistent) return

  emit('clickOutsideOrKeyupEscape')
}

function createCancelButton(): DialogCardFooterInterface {
  function getClickFunction() {
    return async () => {
      if (typeof properties.cancel === 'object' && properties.cancel.click) {
        const doClose = await properties.cancel.click()

        if (doClose === false) {
          return
        }

        onClickCancel()
      } else {
        onClickCancel()
      }
    }
  }

  const clickFunction = getClickFunction()

  return typeof properties.cancel === 'object'
    ? {
        position: 'left',
        variant: 'ghost',
        ...properties.cancel,
        title: properties.cancel.title === undefined ? t('cancel') : properties.cancel.title,
        click: clickFunction,
      }
    : {
        position: 'left',
        variant: 'ghost',
        title: typeof properties.cancel === 'string' ? properties.cancel : t('cancel'),
        click: onClickCancel,
      }
}

function createConfirmButton(): DialogCardFooterInterface {
  function getClickFunction() {
    return async () => {
      setLoading()
      if (typeof properties.confirm === 'object' && properties.confirm.click) {
        properties.beforeConfirm?.()
        const doClose = await properties.confirm.click()

        if (doClose === false) {
          setSuccess()
          return
        }

        onClickConfirm()
        setLoading(false)
        properties.afterConfirm?.()
      } else {
        onClickConfirm()
      }
    }
  }

  const clickFunction = getClickFunction()

  return typeof properties.confirm === 'object'
    ? {
        position: 'right',
        variant: 'default',
        loading: state.value === 'loading',
        ...properties.confirm,
        title: properties.confirm.title === undefined ? t('confirm') : properties.confirm.title,
        click: clickFunction,
      }
    : {
        title: typeof properties.confirm === 'string' ? properties.confirm : t('confirm'),
        position: 'right',
        loading: state.value === 'loading',
        variant: 'default',
        click: onClickConfirm,
      }
}

function close(): void {
  emit('close')
}

function setState(newState: ADialogState) {
  state.value = newState
}

function setLoading(loading: boolean = true) {
  setState(loading ? 'loading' : 'idle')
}

function setSuccess() {
  setState('success')
}

function setError() {
  setState('error')
}
// end Functions

// Async Api functions
// end Async Api functions

// Lifecycle Hooks / Init
// end Lifecycle Hooks / Init
</script>

<template>
  <ADialogBase :class="properties.class" :title="title" :type="type" @click-outside-or-keyup-escape="onClickOutsideOrKeyupEscape">
    <template #portal>
      <slot name="portal" />
    </template>

    <template #content>
      <slot name="content" />
    </template>

    <slot name="wrapper">
      <ADialogCard
        :actions="[{ icon: WindowCloseIcon, click: close, size: 'sm', variant: 'outline' }]"
        :cancel
        :confirm
        :content-collapse="contentCollapse"
        :description="description"
        :footer="computedActions"
        is-dialog-card
        :text="text"
        :title="title"
      >
        <slot name="title" />
        <slot name="description" />
        <slot name="default" />
      </ADialogCard>
    </slot>
  </ADialogBase>
</template>

<style lang="scss">
.atabix-dialog__container {
  @apply flex;
  @apply items-center;
  @apply justify-center;
}
</style>
